import { ChevronDown, ChevronRight, Plus } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Flex, Text, IconButton, Button } from "@radix-ui/themes";
import {
  DndContext,
  closestCenter,
  pointerWithin,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
  MeasuringStrategy,
  useDroppable,
  type CollisionDetection,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import SideDrawer from "@/components/ui/SideDrawer";
import { SortableItem } from "@/components/dnd";
import { menuService } from "@/services/breeze/MenuService";
import { CreateUpdateMenuItem, MenuItem } from "@/types/breeze/Menu";
import withTenantAdminRequired from "@/hoc/withTenantAdminRequired";
import MenuItemCard from "./MenuItemCard";
import MenuItemForm from "./MenuItemForm";

// ── Helpers ──────────────────────────────────────────────────────────

function findItemFlat(roots: MenuItem[], id: number): MenuItem | undefined {
  for (const root of roots) {
    if (root.id === id) return root;
    for (const child of root.items) {
      if (child.id === id) return child;
    }
  }
  return undefined;
}

function cloneTree(roots: MenuItem[]): MenuItem[] {
  return roots.map((r) => ({ ...r, items: r.items.map((c) => ({ ...c })) }));
}

function buildUpdates(roots: MenuItem[]) {
  const updates: { item_id: number; sort_order: number; parent_id: number | null }[] = [];
  roots.forEach((root, ri) => {
    updates.push({ item_id: root.id, sort_order: ri, parent_id: null });
    root.items.forEach((child, ci) => {
      updates.push({ item_id: child.id, sort_order: ci, parent_id: root.id });
    });
  });
  return updates;
}

/** Remove an item from its current location in the tree */
function removeItem(roots: MenuItem[], itemId: number, parentId: number | null) {
  if (parentId === null) {
    const idx = roots.findIndex((r) => r.id === itemId);
    if (idx < 0) return null;
    const [removed] = roots.splice(idx, 1);
    // Promote children to root
    const promoted = removed.items.map((c) => ({ ...c, parent_id: null, items: [] as MenuItem[] }));
    roots.splice(idx, 0, ...promoted);
    return { ...removed, items: [] as MenuItem[] };
  }
  const parent = roots.find((r) => r.id === parentId);
  if (!parent) return null;
  const idx = parent.items.findIndex((c) => c.id === itemId);
  if (idx < 0) return null;
  return parent.items.splice(idx, 1)[0];
}

// ── Collision detection ──────────────────────────────────────────────

const menuCollisionDetection: CollisionDetection = (args) => {
  const centerCollisions = closestCenter(args);
  if (centerCollisions.length > 0 && !centerCollisions[0].id.toString().startsWith("children-")) {
    return centerCollisions;
  }
  const childZone = pointerWithin(args).find((c) => c.id.toString().startsWith("children-"));
  if (childZone) return [childZone];
  return centerCollisions;
};

// ── Child drop zone ─────────────────────────────────────────────────

function ChildDropZone({ parentId, hasChildren, children }: {
  parentId: number;
  hasChildren: boolean;
  children: React.ReactNode;
}) {
  const { setNodeRef } = useDroppable({ id: `children-${parentId}` });

  if (!hasChildren) {
    return <div ref={setNodeRef} style={{ marginLeft: "2rem", height: "4px" }} />;
  }
  return (
    <Box ref={setNodeRef} style={{ marginLeft: "2rem", marginTop: "var(--space-1)" }}>
      {children}
    </Box>
  );
}

// ── Main component ──────────────────────────────────────────────────

const MenuEditor = withTenantAdminRequired(function MenuEditor({
  triggerAddItem = false,
  onAddItemTriggered,
  hideWhenEmpty = false,
}: {
  triggerAddItem?: boolean;
  onAddItemTriggered?: () => void;
  hideWhenEmpty?: boolean;
} = {}) {
  const [searchParams] = useSearchParams();
  const menuId = parseInt(searchParams.get("menuId") as string);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingMenuItem, setEditingMenuItem] = useState<MenuItem | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (triggerAddItem) { setIsDrawerOpen(true); onAddItemTriggered?.(); }
  }, [triggerAddItem, onAddItemTriggered]);

  const { data: menu, isLoading, error } = useQuery({
    queryKey: ["menu", menuId],
    queryFn: () => menuService.getMenu(menuId),
  });

  // ── Internal DnD state ────────────────────────────────────────────

  const [internalRoots, setInternalRoots] = useState<MenuItem[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const isReordering = useRef(false);
  const hasCrossContainerMove = useRef(false);
  const rootsRef = useRef<MenuItem[]>([]);

  const updateRoots = useCallback((newRoots: MenuItem[]) => {
    rootsRef.current = newRoots;
    setInternalRoots(newRoots);
  }, []);

  useEffect(() => {
    if (!menu) return;
    const sorted = [...menu.items]
      .filter((i) => i.parent_id === null)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((root) => ({
        ...root,
        items: root.items.slice().sort((a, b) => a.sort_order - b.sort_order),
      }));
    if (!isReordering.current) updateRoots(sorted);
    isReordering.current = false;
  }, [menu, updateRoots]);

  const activeItem = useMemo(
    () => (activeId != null ? findItemFlat(internalRoots, activeId) : null),
    [activeId, internalRoots],
  );

  // ── Expand / collapse ─────────────────────────────────────────────

  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const toggleExpand = useCallback((id: number) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  // ── Mutations ─────────────────────────────────────────────────────

  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["menu", menuId] });
    queryClient.invalidateQueries({ queryKey: ["menus"] });
    queryClient.invalidateQueries({ queryKey: ["my-menu"] });
  }, [queryClient, menuId]);

  const createMenuItem = useMutation({
    mutationFn: (data: CreateUpdateMenuItem) => menuService.createMenuItem(menuId, data),
    onSuccess: () => { invalidateAll(); setIsDrawerOpen(false); setEditingMenuItem(null); },
  });

  const updateMenuItem = useMutation({
    mutationFn: ({ itemId, data }: { itemId: number; data: CreateUpdateMenuItem }) =>
      menuService.updateMenuItem(menuId, itemId, data),
    onSuccess: () => { invalidateAll(); setIsDrawerOpen(false); setEditingMenuItem(null); },
  });

  const deleteMenuItem = useMutation({
    mutationFn: (itemId: number) => menuService.deleteMenuItem(menuId, itemId),
    onSuccess: () => invalidateAll(),
  });

  const reorderMenuItems = useMutation({
    mutationFn: (updates: { item_id: number; sort_order: number; parent_id: number | null }[]) =>
      menuService.reorderMenuItems(menuId, updates),
    onMutate: async () => { await queryClient.cancelQueries({ queryKey: ["menu", menuId] }); },
    onSettled: () => invalidateAll(),
  });

  // ── DnD handlers ──────────────────────────────────────────────────

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(Number(event.active.id));
    hasCrossContainerMove.current = false;
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeIdNum = Number(active.id);
    const overId = over.id.toString();
    const current = rootsRef.current;
    const activeItem = findItemFlat(current, activeIdNum);
    if (!activeItem) return;

    // ── Over a child drop zone → move into that parent ──
    if (overId.startsWith("children-")) {
      const targetParentId = Number(overId.replace("children-", ""));
      if (targetParentId === activeIdNum || activeItem.parent_id === targetParentId) return;

      const newRoots = cloneTree(current);
      const moved = removeItem(newRoots, activeIdNum, activeItem.parent_id);
      if (!moved) return;

      const newParent = newRoots.find((r) => r.id === targetParentId);
      if (newParent) newParent.items.push({ ...moved, parent_id: targetParentId });

      setCollapsed((prev) => {
        if (!prev.has(targetParentId)) return prev;
        const next = new Set(prev);
        next.delete(targetParentId);
        return next;
      });

      hasCrossContainerMove.current = true;
      updateRoots(newRoots);
      return;
    }

    // ── Over a sortable item ──
    const overItem = findItemFlat(current, Number(overId));
    if (!overItem) return;

    // Same container → SortableContext handles it
    if (activeItem.parent_id === overItem.parent_id) return;
    // Both root → just reorder
    if (activeItem.parent_id === null && overItem.parent_id === null) return;

    // Child over root → promote to root
    if (activeItem.parent_id !== null && overItem.parent_id === null) {
      const newRoots = cloneTree(current);
      const moved = removeItem(newRoots, activeIdNum, activeItem.parent_id);
      if (!moved) return;

      const overIdx = newRoots.findIndex((r) => r.id === overItem.id);
      newRoots.splice(overIdx >= 0 ? overIdx : newRoots.length, 0, {
        ...moved,
        parent_id: null,
        items: [],
      });

      hasCrossContainerMove.current = true;
      updateRoots(newRoots);
      return;
    }

    // Child over child of different parent → move to that parent
    if (activeItem.parent_id !== null && overItem.parent_id !== null && activeItem.parent_id !== overItem.parent_id) {
      const newRoots = cloneTree(current);
      const moved = removeItem(newRoots, activeIdNum, activeItem.parent_id);
      if (!moved) return;

      const newParent = newRoots.find((r) => r.id === overItem.parent_id);
      if (newParent) {
        const idx = newParent.items.findIndex((c) => c.id === Number(overId));
        newParent.items.splice(idx >= 0 ? idx : newParent.items.length, 0, {
          ...moved,
          parent_id: overItem.parent_id,
        });
      }

      hasCrossContainerMove.current = true;
      updateRoots(newRoots);
    }
  }, [updateRoots]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    const hadCrossMove = hasCrossContainerMove.current;
    setActiveId(null);
    hasCrossContainerMove.current = false;

    if (hadCrossMove) {
      isReordering.current = true;
      reorderMenuItems.mutate(buildUpdates(rootsRef.current));
      return;
    }

    if (!over || active.id === over.id) return;
    if (over.id.toString().startsWith("children-")) {
      isReordering.current = true;
      reorderMenuItems.mutate(buildUpdates(rootsRef.current));
      return;
    }

    const activeIdNum = Number(active.id);
    const overIdNum = Number(over.id);
    const current = rootsRef.current;
    const activeItem = findItemFlat(current, activeIdNum);
    const overItem = findItemFlat(current, overIdNum);
    if (!activeItem || !overItem || activeItem.parent_id !== overItem.parent_id) return;

    const newRoots = cloneTree(current);
    if (activeItem.parent_id === null) {
      const oldIdx = newRoots.findIndex((r) => r.id === activeIdNum);
      const newIdx = newRoots.findIndex((r) => r.id === overIdNum);
      if (oldIdx >= 0 && newIdx >= 0) {
        const reordered = arrayMove(newRoots, oldIdx, newIdx);
        isReordering.current = true;
        updateRoots(reordered);
        reorderMenuItems.mutate(buildUpdates(reordered));
      }
    } else {
      const parent = newRoots.find((r) => r.id === activeItem.parent_id);
      if (parent) {
        const oldIdx = parent.items.findIndex((c) => c.id === activeIdNum);
        const newIdx = parent.items.findIndex((c) => c.id === overIdNum);
        if (oldIdx >= 0 && newIdx >= 0) parent.items = arrayMove(parent.items, oldIdx, newIdx);
      }
      isReordering.current = true;
      updateRoots(newRoots);
      reorderMenuItems.mutate(buildUpdates(newRoots));
    }
  }, [reorderMenuItems, updateRoots]);

  // ── Handlers ──────────────────────────────────────────────────────

  const handleEditMenuItem = (item: MenuItem) => { setEditingMenuItem(item); setIsDrawerOpen(true); };

  const handleSubmit = (values: CreateUpdateMenuItem) => {
    if (editingMenuItem) {
      updateMenuItem.mutate({ itemId: editingMenuItem.id, data: values });
    } else {
      createMenuItem.mutate(values);
    }
  };

  // ── Render ────────────────────────────────────────────────────────

  if (isLoading) return <Box p="4"><Text>Loading...</Text></Box>;
  if (error) return <Box p="4"><Text>Error loading menu items</Text></Box>;
  if (!menu) return null;

  const rootIds = internalRoots.map((r) => r.id);
  const isDragging = activeId != null;

  return (
    <Box px="4">
      {!(hideWhenEmpty && internalRoots.length === 0) && (
        <Flex justify="between" align="center" mb="4">
          <Text size="4" weight="bold">{menu.name}</Text>
          <Button onClick={() => setIsDrawerOpen(true)} size="1">
            <Plus style={{ width: "1rem", height: "1rem" }} />
            Add Menu Item
          </Button>
        </Flex>
      )}

      {!(hideWhenEmpty && internalRoots.length === 0) && (
        <DndContext
          sensors={sensors}
          collisionDetection={menuCollisionDetection}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        >
          <SortableContext items={rootIds} strategy={verticalListSortingStrategy}>
            <Box style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
              {internalRoots.map((root) => {
                const isExpanded = !collapsed.has(root.id);
                return (
                  <Box key={root.id}>
                    <SortableItem id={root.id} disabled={root.locked}>
                      {({ dragHandleProps }) => (
                        <MenuItemCard
                          item={root}
                          onEdit={handleEditMenuItem}
                          onDelete={(id) => deleteMenuItem.mutate(id)}
                          dragHandleProps={dragHandleProps}
                          additionalTitleContent={
                            root.items.length > 0 ? (
                              <IconButton variant="ghost" size="1" onClick={() => toggleExpand(root.id)}>
                                {isExpanded
                                  ? <ChevronDown style={{ width: "1rem", height: "1rem", color: "var(--gray-9)" }} />
                                  : <ChevronRight style={{ width: "1rem", height: "1rem", color: "var(--gray-9)" }} />}
                              </IconButton>
                            ) : undefined
                          }
                        />
                      )}
                    </SortableItem>

                    {(isExpanded || isDragging) && (
                      <ChildDropZone parentId={root.id} hasChildren={root.items.length > 0 && isExpanded}>
                        {isExpanded && root.items.length > 0 && (
                          <SortableContext items={root.items.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                            <Box style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
                              {root.items.map((child) => (
                                <SortableItem key={child.id} id={child.id} disabled={child.locked}>
                                  {({ dragHandleProps }) => (
                                    <MenuItemCard
                                      item={child}
                                      onEdit={handleEditMenuItem}
                                      onDelete={(id) => deleteMenuItem.mutate(id)}
                                      dragHandleProps={dragHandleProps}
                                    />
                                  )}
                                </SortableItem>
                              ))}
                            </Box>
                          </SortableContext>
                        )}
                      </ChildDropZone>
                    )}
                  </Box>
                );
              })}
            </Box>
          </SortableContext>

          <DragOverlay dropAnimation={null}>
            {activeItem ? (
              <Box style={{ transform: "rotate(2deg)", boxShadow: "0 8px 24px rgba(0,0,0,0.2)", borderRadius: "var(--radius-3)", backgroundColor: "var(--color-background)" }}>
                <MenuItemCard item={activeItem} onEdit={handleEditMenuItem} onDelete={(id) => deleteMenuItem.mutate(id)} />
                {activeItem.parent_id === null && activeItem.items.length > 0 && (
                  <Box style={{ marginLeft: "2rem", display: "flex", flexDirection: "column", gap: "var(--space-1)", paddingBottom: "var(--space-2)" }}>
                    {activeItem.items.map((child) => (
                      <MenuItemCard key={child.id} item={child} onEdit={handleEditMenuItem} onDelete={(id) => deleteMenuItem.mutate(id)} />
                    ))}
                  </Box>
                )}
              </Box>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      <SideDrawer
        isOpen={isDrawerOpen}
        onClose={() => { setIsDrawerOpen(false); setEditingMenuItem(null); }}
        title={editingMenuItem ? "Edit Menu Item" : "Create New Menu Item"}
      >
        <MenuItemForm
          editingMenuItem={editingMenuItem}
          allItems={menu?.items ?? []}
          isPending={createMenuItem.isPending || updateMenuItem.isPending}
          onSubmit={handleSubmit}
        />
      </SideDrawer>
    </Box>
  );
});

export default MenuEditor;
